import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileData,
  FileSection,
  FileSelection,
  FileSelectionState,
  GoogleFontIcon,
  Progress,
  UpdateFileNamePayload,
} from '@asset-metrix/portal-ui';
import { JsonError, JsonSchema } from '@sagold/react-json-editor';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';

import { FilePreview } from './filePreview/FilePreview';
import { useDocUploadStore } from './store/store';
import {
  METADATA_EXTRACTION_FROM_AI_VALUE,
  METADATA_EXTRACTION_TYPE,
} from './constants';
import { useFilePreviewState, useMetadataInputsDisabled } from './hooks';
import { MetaDataInputField } from './MetaDataInputField';

import styles from '../docUpload/doc-upload-modal.module.scss';

const AIExtractionProgressBar = ({
  fileState,
}: {
  fileState: FileSelectionState;
}) => {
  const { aiMetadataExtractionStatus } = useDocUploadStore();
  const totalItems = fileState.length;
  const completedItems = fileState.filter((selection) => {
    if (selection.file.type === 'application/pdf') {
      return ['FAILED', 'SUCCESS'].includes(
        aiMetadataExtractionStatus[selection.id]
      );
    }
    return true;
  }).length;

  return (
    <div className={styles['top-info']}>
      <Progress
        completedItems={completedItems}
        svgIconName="aiIcon"
        getProgressText={(completedItems, totalItems) =>
          `${completedItems} of ${totalItems} files`
        }
        totalItems={totalItems}
      />
    </div>
  );
};

const DisplayUploadProgress = ({
  fileState,
}: {
  fileState: FileSelectionState;
}) => {
  const { t } = useTranslation();

  const metaDataExtractionInfo = useMemo(() => {
    const successCount = fileState.filter(
      (f) => f.uploadState === 'isSuccess'
    ).length;
    const problemCount = fileState.filter(
      (f) => f.uploadState === 'isError'
    ).length;
    const totalFiles = fileState.length;

    if (successCount > 0 || problemCount > 0) {
      const progressMessage = t('app.upload.info.progress', '', {
        count: successCount,
        total: totalFiles,
      });

      const problemMessage = problemCount
        ? t('app.upload.info.problemCount', {
            count: problemCount,
            total: totalFiles,
          })
        : '';

      return (
        <div className={clsx(styles['upload-info'], styles['top-info'])}>
          <GoogleFontIcon name="info" solid />
          {progressMessage} {problemMessage}
        </div>
      );
    }

    return null;
  }, [fileState, t]);

  return metaDataExtractionInfo;
};

export const RenderMetaDataExtractedView = ({
  editorInitialData,
  extractionMethod,
  jsonSchema,
  onEditorDataChange,
  onFileRemove,
  userEnabledPatternBasedRenaming,
  preFilter,
  setErrors,
  upload,
}: {
  editorInitialData: Record<string, unknown>;
  extractionMethod: METADATA_EXTRACTION_TYPE;
  jsonSchema: JsonSchema | undefined;
  onEditorDataChange: (fileId: string, data: Record<string, unknown>) => void;
  onFileRemove: (file: FileData) => void;
  userEnabledPatternBasedRenaming: boolean;
  preFilter: Record<string, string | string[] | null>;
  setErrors: (fileId: string, error: JsonError[]) => void;
  upload: (file: FileSelection) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const {
    fileState,
    dispatchFileStateAction,
    setPatternBasedFilename,
    setRenameFileWithPattern,
  } = useDocUploadStore(
    useShallow((state) => ({
      fileState: state.fileState,
      dispatchFileStateAction: state.dispatchFileStateAction,
      setPatternBasedFilename: state.setPatternBasedFilename,
      setRenameFileWithPattern: state.setRenameFileWithPattern,
    }))
  );
  const metadataInputsDisabled = useMetadataInputsDisabled();
  const isPending = useMemo(
    () => fileState.some((selection) => selection.uploadState === 'isPending'),
    [fileState]
  );

  const onFileNameChange = useCallback(
    ({ id, name, cleared = false }: UpdateFileNamePayload) => {
      dispatchFileStateAction({
        type: 'updateFileName',
        payload: {
          id,
          name,
        },
      });

      if (userEnabledPatternBasedRenaming) {
        setRenameFileWithPattern(id, cleared);
        if (cleared) {
          const { perFileMetaData } = useDocUploadStore.getState();
          setPatternBasedFilename(id, perFileMetaData[id] ?? {});
        }
      }
    },
    [
      dispatchFileStateAction,
      userEnabledPatternBasedRenaming,
      setPatternBasedFilename,
      setRenameFileWithPattern,
    ]
  );

  const onDataChanged = useCallback(
    (fileId: string, data: Record<string, unknown>) => {
      onEditorDataChange(fileId, data);
    },
    [onEditorDataChange]
  );

  const generateMetaDataExtractionInput = useMemo(
    () => (fileData: FileSelection) => {
      if (!jsonSchema) {
        return;
      }

      return (
        <MetaDataInputField
          editorInitialData={editorInitialData}
          extractionMethod={extractionMethod}
          file={fileData}
          jsonSchema={jsonSchema}
          onEditorDataChange={onDataChanged}
          preFilter={preFilter}
          setErrors={setErrors}
        />
      );
    },
    [
      jsonSchema,
      editorInitialData,
      extractionMethod,
      onDataChanged,
      preFilter,
      setErrors,
    ]
  );

  const filePreviewState = useFilePreviewState();

  if (fileState.length === 0) {
    return <div>{t('app.upload.noFiles')}</div>;
  }

  return (
    <div className={styles['metadata-extraction-container-second-step']}>
      <DisplayUploadProgress fileState={fileState} />
      {extractionMethod === METADATA_EXTRACTION_FROM_AI_VALUE && (
        <AIExtractionProgressBar fileState={fileState} />
      )}
      <div
        className={styles['metadata-extraction-container-second-step__content']}
      >
        <FileSection
          files={fileState}
          isNameEditable={!metadataInputsDisabled}
          onFileClicked={(id, fileData) => {
            filePreviewState.setSelectedFile(id, fileData.file);
          }}
          onFileNameChange={onFileNameChange}
          onRemoveFile={(file) => {
            if (
              filePreviewState.isOpen &&
              'id' in file &&
              filePreviewState.fileId === file.id
            ) {
              filePreviewState.close();
            }
            onFileRemove(file);
          }}
          selectedFileId={
            filePreviewState.isOpen ? filePreviewState.fileId : undefined
          }
          showPagination={true}
          showUploadState={isPending}
          title={t('app.upload.extractMetaData.title')}
          uploadFile={upload}
          metaDataInputLabel="Form Data"
          metaDataExtractionInput={generateMetaDataExtractionInput}
          withPreview
        />

        {filePreviewState.isOpen && (
          <FilePreview
            fileId={filePreviewState.fileId}
            fileName={filePreviewState.file.name}
            fileType={filePreviewState.file.type}
            previewUrl={filePreviewState.previewUrl}
            onClose={filePreviewState.close}
            extractionMethod={extractionMethod}
          />
        )}
      </div>
    </div>
  );
};
